import cx from 'classnames';
import type { FunctionComponent } from 'react';
import styles from './ReadOnlySection.module.less';

type Props = {
  isReadOnly?: boolean;
  className?: string;
  /**
   * If true, the cursor for the section will have the pointer style.
   */
  isClickable?: boolean;
};

/**
 * ReadOnlySection is a section which (when specified) will "grey out"
 * a section of the page and to imply that it is read-only.
 * Note that you still need to disable any inputs/buttons in the `children`
 * to fully prevent user interaction.
 *
 * @example
 * const MyComponent = () => {
 *   const [isEnabled, toggleEnabled] = useState(false);
 *   return (
 *     <>
 *       <Checkbox label="Enable" checked={isEnabled} onChange={toggleEnabled} />
 *       <ReadOnlySection isReadOnly={!isEnabled}>
 *         <Input label="Name" value="My Name" isDisabled={!isEnabled} />
 *         <p>This section is read-only when the checkbox is not checked.</p>
 *       </ReadOnlySection>
 *     </>
 *   );
 * };
 */
const ReadOnlySection: FunctionComponent<Props> = ({
  children,
  isReadOnly,
  //  Optionally pass additional styles to the section.
  //  This is intended for things like adding padding to the section,
  //  not attempting to override the read-only styles.
  className,
  isClickable,
}) => (
  <section
    className={cx(styles.readOnlySection, className, {
      [styles.isReadOnly]: isReadOnly,
      [styles.clickable]: isClickable,
    })}
    data-testid="read-only-section"
  >
    {children}
  </section>
);

export default ReadOnlySection;
